import { z } from 'zod';

import { listQuerySchema, sortDirSchema } from './common.js';
import { creditEntryTypeSchema, type CreditEntryType, type SubscriptionStatus } from './enums.js';
import type { PlanTier } from './plans.js';

/**
 * BILLING — abonelik, ödeme sağlayıcısı, kredi defteri, entitlement.
 *
 * Tasarım §7 (planlar ve yumuşak sınır), §8 (kredi sistemi).
 *
 * ---------------------------------------------------------------------------
 * Bu alanın diğerlerine verdiği İKİ SÖZLEŞME
 * ---------------------------------------------------------------------------
 *  1. `CreditService.reserve / commit / refund` — creative ve (ileride) agent
 *     kendi kredi mantığını YAZMAZ, bunları çağırır.
 *  2. `EntitlementService.can(workspaceId, capability)` — automation ve
 *     creative her çalıştırmadan önce sorar; kendi plan mantığını yazmaz.
 *
 * İkisi de aşağıda imzasıyla tanımlı. Belirsiz bırakılırsa iki alan iki farklı
 * kredi aritmetiği yazar ve fark, kullanıcının parasında görünür.
 *
 * ---------------------------------------------------------------------------
 * Yumuşak sınır (§7) — "veri rehin alınmaz"
 * ---------------------------------------------------------------------------
 * Harcama limiti aşılınca: uyarı + `status = GRACE` + `graceUntil = now + 7g`.
 * Grace bitince OTOMASYON ve KREATİF ÜRETİMİ durur; dashboard, raporlar ve
 * dışa aktarma AÇIK KALIR. Kullanıcının kendi verisini ödeme baskısı için
 * rehin almak, ürünü terk etmesinin en hızlı yolu.
 */

// ═══════════════════════════════════════════════════════════════════════════
// Abonelik durumu
// ═══════════════════════════════════════════════════════════════════════════

export interface SubscriptionStatusResponse {
  /**
   * 🔴 `null` = ÇALIŞMA ALANINDA HİÇ PLAN YOK.
   *
   * Yeni çalışma alanı plansız açılıyor (9 Eyl 2026) ve plansızlık, abonelik
   * SATIRININ YOKLUĞU ile temsil ediliyor. Yanıtın kendisi yine dönüyor —
   * `null` gövde döndürseydik arayüz "plan yok" ile "istek başarısız"ı
   * ayırt edemezdi ve ikisine aynı boş ekranı basardı.
   *
   * Bitmiş deneme bundan FARKLI: orada satır var, `plan: TRIAL` +
   * `status: EXPIRED`. Ekranda "planınız sona erdi, yenileyin" der;
   * burada "bu çalışma alanı için plan seçin".
   *
   * `WorkspaceContextResponse.plan` de aynı sözleşmeyi kullanıyor — kabuk
   * rozetiyle faturalama ekranı aynı gerçeği aynı biçimde okuyor.
   */
  plan: PlanTier | null;
  status: SubscriptionStatus | null;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  graceUntil: string | null;
  /// FT9 deneme sayacı. Negatif = süresi geçmiş.
  trialDaysRemaining: number | null;
  /// Limitler abonelik SATIRINDAN okunur, plan tablosundan değil — plan
  /// fiyatları değişince mevcut abonenin kotası kaymasın (§7).
  /// Plan yoksa `null`: sıfır yazmak "limitiniz 0" gibi okunurdu, oysa
  /// ortada henüz bir limit KAVRAMI yok.
  limits: {
    adSpendLimit: number;
    monthlyCredits: number;
    maxAdAccounts: number;
    maxMembers: number;
  } | null;
  /**
   * Ödeme sağlayıcısında bir abonelik kaydına bağlı mı.
   *
   * `providerCustomerId` / `providerSubscriptionId` yanıta GİRMEZ — arayüzde
   * yapacakları bir iş yok, sızdırılacak değerleri var. (Aynı karar
   * `admin.ts`'te de verilmişti.)
   *
   * ⚠️ Faz 8'de `stripeLinked` iken bu ada geçti: sağlayıcı iyzico oldu ve
   * alan adı yalan söylemeye başlamıştı.
   */
  paymentLinked: boolean;
  /**
   * Kaydı hangi sağlayıcı tutuyor ("iyzico"). `paymentLinked` false ise null.
   *
   * Neden ekranda lazım: iptal ve kart değiştirme akışları sağlayıcıya göre
   * farklı yerlere gidiyor (iyzico'da Stripe'ınki gibi bir müşteri portalı
   * YOK). Arayüzün bunu env'den ya da tahminden okuması gerekmesin.
   */
  paymentProvider: string | null;
  /// Ödeme sağlayıcısı yapılandırılmış mı. `false` ise arayüz "yükselt"
  /// düğmesini gösterir ama tıklanınca `PAYMENT_NOT_CONFIGURED` gelir;
  /// bunu önceden bilmek daha dürüst.
  paymentConfigured: boolean;
}

/// FT2/FT4 — plan karşılaştırma. Kaynak `plans.ts`; burada yalnız yanıt şekli.
export interface PlanCatalogEntry {
  tier: PlanTier;
  /// `null` = özel fiyat (Enterprise).
  monthlyUsd: number | null;
  /// Yıllıkta aylığa düşen tutar — YUVARLANMAZ ($19 → $15.20).
  annualMonthlyUsd: number | null;
  spendCapUsd: number | null;
  spendFloorUsd: number | null;
  credits: number;
  accounts: number;
  members: number;
  /// Ajan günlük mesaj limiti (§8 adil kullanım).
  agentDailyMessages: number;
  current: boolean;
  /// Mevcut plana göre yön — arayüz "Yükselt"/"Düşür" yazsın diye.
  direction: 'upgrade' | 'downgrade' | 'current';
}

export interface PlanCatalogResponse {
  plans: PlanCatalogEntry[];
  annualDiscount: number;
  graceDays: number;
  trial: { days: number; credits: number; accounts: number; members: number };
}

/**
 * §8 "adil kullanım" diyor ama RAKAM VERMİYOR. AJ10 kalan hakkı ekranda
 * gösteriyor, yani bir sayı olmak zorunda. Buradakiler sözleşme kararı:
 * ajan sohbeti 0 kredi, ama günlük mesaj sayısı plana bağlı. `-1` sınırsız.
 */
/**
 * ABONELİĞİ OKUNAMAYAN workspace'in düştüğü sınır.
 *
 * 🔴 `AGENT_DAILY_MESSAGE_LIMIT.TRIAL` DEĞİL ve olmamalı. Yedek eskiden
 * TRIAL'ı işaret ediyordu; TRIAL sınırsıza çekilince (24 Ağu 2026) o bağ,
 * FATURASI OLMAYAN bir hesaba sessizce sınırsız model çağrısı hakkı verdi.
 * İki soru ayrı: "deneme kullanıcısı ne kadar konuşabilir" ürün kararı,
 * "veri tutarsızlığında ne yapılır" güvenlik kararı. Aynı sayıyı
 * paylaştıkları sürece birini değiştiren diğerini de değiştiriyordu.
 */
export const AGENT_DAILY_MESSAGE_FALLBACK = 20;

export const AGENT_DAILY_MESSAGE_LIMIT: Record<PlanTier, number> = {
  /* 🔴 SINIRSIZ — 24 Ağu 2026, kullanıcı kararı. Önceden 20'ydi ve deneme
     hesabında ajanı test etmeyi imkânsız kılıyordu: tek bir kampanya kurma
     turu araç çağrılarıyla birlikte hakkın yarısını yiyordu. Geri konacaksa
     tek yer burası. */
  TRIAL: -1,
  STARTER: 50,
  GROWTH: 150,
  PRO: 500,
  ENTERPRISE: -1,
};

// ═══════════════════════════════════════════════════════════════════════════
// Kullanım ölçümü ve yumuşak sınır
// ═══════════════════════════════════════════════════════════════════════════

export interface UsageMetric {
  used: number;
  /// `-1` sınırsız.
  limit: number;
  remaining: number;
  /// 0–100 arası; sınırsızda null.
  pct: number | null;
}

export interface UsageResponse {
  period: { start: string; end: string };
  /**
   * Fatura döneminde senkronlanan reklam harcaması.
   *
   * 🔴 Para birimi karışımı: limit USD cinsinden ama hesaplar farklı para
   * biriminde olabilir ve `MetricDaily`'de kur yok. `usdComparable: false`
   * ise limit ZORLANMAZ — yanlış kurla yanlış hesaplanmış bir limit yüzünden
   * kullanıcının otomasyonunu durdurmak, limiti hiç uygulamamaktan kötü.
   * O durumda yalnız bilgilendirme bildirimi gider.
   */
  adSpend: UsageMetric & {
    byCurrency: { currency: string; amount: number }[];
    usdComparable: boolean;
  };
  adAccounts: UsageMetric;
  /// Bekleyen davetler DAHİL.
  members: UsageMetric;
  /// Dönem içinde tüketilen kredi (COMMIT + negatif ADJUSTMENT).
  credits: UsageMetric;
}

/**
 * FT6/FT7 — yumuşak sınır makinesi. Zamanlanmış iş bunu günde bir kez
 * değerlendirir; uç aynı hesabı okuma amaçlı sunar.
 */
export interface SoftLimitState {
  /// Limit aşıldı mı.
  exceeded: boolean;
  /// GRACE başladıysa bitiş anı.
  graceUntil: string | null;
  /// Grace bitti mi — bittiyse otomasyon ve kreatif kapalı.
  graceExpired: boolean;
  /// Grace bitince NE DURUR, NE AÇIK KALIR. Arayüz bunu listeler; metni
  /// kendi uydurmaz, yoksa iki ekran iki farklı şey söyler.
  stopped: ('automation' | 'creative' | 'agent_write')[];
  stillAvailable: ('dashboard' | 'reports' | 'export' | 'agent_read')[];
}

// ═══════════════════════════════════════════════════════════════════════════
// 🔴 Entitlement — DİĞER ALANLARIN SORDUĞU SORU
// ═══════════════════════════════════════════════════════════════════════════

/**
 * `EntitlementService.can(workspaceId, action)` → `EntitlementDecision`
 *
 * automation her koşudan önce, creative her üretimden önce, agent her yazma
 * aracından önce bunu sorar. Kendi plan/grace mantığını yazmazlar — yazsalardı
 * dört ayrı yorum olur ve biri grace'i yanlış okur.
 */
export const ENTITLEMENT_ACTIONS = [
  'automation.run',
  'creative.generate',
  'agent.message',
  'agent.write',
  'account.connect',
  'member.invite',
  'export.run',
] as const;
export type EntitlementAction = (typeof ENTITLEMENT_ACTIONS)[number];

export interface EntitlementDecision {
  allowed: boolean;
  /// Reddin makine okunur sebebi. `allowed: true` ise null.
  reasonCode: BillingErrorCode | null;
  /// Ne zaman tekrar açılacak (grace bitişi, dönem başı). Bilinmiyorsa null.
  until: string | null;
  /// Sayısal limit bilgisi — arayüz "3/5 kullanıldı" diyebilsin.
  usage?: { used: number; limit: number };
}

/// `GET /billing/entitlements` — UI aynı gerçeği okur, kendi kuralını yazmaz.
export type EntitlementsResponse = Record<EntitlementAction, EntitlementDecision>;

// ═══════════════════════════════════════════════════════════════════════════
// 🔴 Kredi servisi — reserve → commit / refund
// ═══════════════════════════════════════════════════════════════════════════

/**
 * İKİ AŞAMALI DÜŞÜM (§8). Neden tek aşamalı değil: video render dakikalar
 * sürüyor ve hata verebiliyor. Baştan düşseydik hatada iade etmek gerekirdi
 * ve iade edilmemiş bir hata her zaman mümkün; rezerve edip sonunda
 * kesinleştirmek, hatanın varsayılan sonucunu "para kullanıcıda kalır"
 * yapıyor.
 *
 * ───────────────────────────── İMZALAR ─────────────────────────────
 *
 * reserve(input: CreditReserveInput): Promise<CreditReservation>
 *   Tek transaction:
 *     - `SELECT 1 FROM "credit_balances" WHERE "workspaceId" = $1 FOR UPDATE`
 *       (READ COMMITTED'ta kayıp güncellemeyi engeller — mevcut admin
 *       kodundaki kalıbın aynısı)
 *     - available = planCredits + topupCredits − reservedCredits
 *     - yetmezse `INSUFFICIENT_CREDITS` + `{required, available}`
 *     - reservedCredits += amount
 *     - `RESERVE` defter satırı (amount NEGATİF), `balanceAfter` = plan+topup
 *   Döner: `{ reservationId }` = o defter satırının kimliği. Şemada ayrı
 *   rezervasyon tablosu YOK; `[referenceType, referenceId]` indeksi bunu
 *   taşıyor.
 *
 * commit(reservationId: string): Promise<CreditReservation>
 *   - reservedCredits −= amount
 *   - Düşüm ÖNCE `planCredits`ten, yetmezse `topupCredits`ten. Sıra önemli:
 *     plan kredisi dönem sonunda buharlaşıyor (§8), önce o harcanmalı; tersi
 *     kullanıcıya para kaybettirir.
 *   - `COMMIT` satırı; `balanceAfter = planCredits + topupCredits`
 *     (rezerve HARİÇ — `admin.ts`'teki tanımla birebir aynı)
 *   - Aynı rezervasyon İKİ KEZ commit edilemez (`RESERVATION_ALREADY_SETTLED`)
 *
 * refund(reservationId: string, reason: string): Promise<CreditReservation>
 *   - reservedCredits −= amount (kredi geri açılır)
 *   - `REFUND` satırı (amount POZİTİF)
 *   - `CreativeJob.refundEntryId` doldurulur — şemadaki ÇİFT İADE koruması
 *   - İDEMPOTENT: `refundEntryId` doluysa NO-OP, hata değil. Render hatası,
 *     timeout ve iptal üçü de bu yolu kullanır ve üçü aynı anda tetiklenebilir.
 *
 * Defter satırı ASLA güncellenmez, ASLA silinmez. Düzeltme yeni satırdır.
 */
export interface CreditReserveInput {
  workspaceId: string;
  /// Pozitif tam sayı.
  amount: number;
  /// `creative_job` | `agent_message` | `export` …
  referenceType: string;
  referenceId: string;
  /// Aynı anahtarla ikinci çağrı yeni rezervasyon AÇMAZ, mevcut olanı döner.
  idempotencyKey: string;
  description?: string;
}

export interface CreditReservation {
  reservationId: string;
  workspaceId: string;
  amount: number;
  state: 'reserved' | 'committed' | 'refunded';
  balanceAfter: number;
  createdAt: string;
}

/// KR1 — bakiye kartı.
export interface CreditBalanceResponse {
  planCredits: number;
  topupCredits: number;
  reservedCredits: number;
  /// plan + topup − reserved.
  available: number;
  /// Dönem sonunda düşecek = `planCredits` (devretmez, §8).
  expiringAtPeriodEnd: number;
  periodEnd: string;
  /// Bu dönemin plan kotası — "150 kredinin 40'ı kaldı" için.
  monthlyCredits: number;
  lowBalance: boolean;
}

/**
 * KR8 — düşük bakiye eşiği. Şemada kolon yok; sabit burada.
 * Dönem başına TEK bildirim (dedupe anahtarı: `credit_low:<ws>:<periodStart>`);
 * her kredi harcamasında uyarı göndermek bildirim listesini kullanılamaz yapar.
 */
export const LOW_BALANCE_ABSOLUTE = 5;
export const LOW_BALANCE_PCT_OF_MONTHLY = 0.1;

export function isLowBalance(available: number, monthlyCredits: number): boolean {
  if (monthlyCredits < 0) return false;
  return available < Math.max(LOW_BALANCE_ABSOLUTE, monthlyCredits * LOW_BALANCE_PCT_OF_MONTHLY);
}

export const ledgerListQuerySchema = listQuerySchema.extend({
  type: creditEntryTypeSchema.optional(),
  from: z.iso.datetime().optional(),
  to: z.iso.datetime().optional(),
  referenceType: z.string().max(40).optional(),
  sort: z.enum(['createdAt', 'amount']).default('createdAt'),
  dir: sortDirSchema,
});

export interface LedgerRow {
  id: string;
  type: CreditEntryType;
  /// Harcamada negatif, yüklemede pozitif.
  amount: number;
  balanceAfter: number;
  description: string | null;
  referenceType: string | null;
  referenceId: string | null;
  /// KR3 — kaynağa sıçrama. Çözülmüş hedef; silinmişse null.
  reference: {
    label: string;
    /// Arayüz rotayı bundan üretir; ham URL saklanmaz.
    entity: 'creative' | 'creative_job' | 'workflow' | 'export' | 'admin' | 'agent';
    id: string;
  } | null;
  createdAt: string;
}

/// KR7 — dönem içi tüketim grafiği (gün bazlı).
export interface CreditUsagePoint {
  date: string;
  /// Harcanan kredi (pozitif sayı olarak).
  spent: number;
  granted: number;
  refunded: number;
}

// ═══════════════════════════════════════════════════════════════════════════
// Ek kredi paketleri
// ═══════════════════════════════════════════════════════════════════════════

/**
 * §8 "ek kredi paketi satın alınabilir" diyor ama BOYUT VE FİYAT HİÇBİR YERDE
 * TANIMLI DEĞİL. KR5 bu veriye dayanıyor; katalog olmadan ekran yazılamaz.
 * Aşağısı sözleşme kararı — plan fiyatları gibi bunlar da hipotez ve beta
 * geri bildirimiyle kalibre edilecek.
 *
 * Paket kredisi DEVREDER (`topupCredits`), aylık kota DEVRETMEZ (`planCredits`).
 */
export const CREDIT_PACKS = [
  { id: 'pack_50', credits: 50, priceUsd: 9 },
  { id: 'pack_150', credits: 150, priceUsd: 25 },
  { id: 'pack_500', credits: 500, priceUsd: 75 },
  { id: 'pack_1500', credits: 1500, priceUsd: 200 },
] as const;
export type CreditPackId = (typeof CREDIT_PACKS)[number]['id'];
export const creditPackIdSchema = z.enum(['pack_50', 'pack_150', 'pack_500', 'pack_1500']);

export const topupPurchaseSchema = z.object({
  packId: creditPackIdSchema,
});
export type TopupPurchaseInput = z.infer<typeof topupPurchaseSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// Ödeme sağlayıcısı — soyutlama arkasında
// ═══════════════════════════════════════════════════════════════════════════

/**
 * `PaymentProvider` arayüzü: `createCheckoutSession`, `createTopupSession`,
 * `createPortalSession`, `cancelSubscription`, `resolveCheckoutResult`,
 * `verifyWebhook`, `applyWebhookEvent`.
 *
 * Sağlayıcı İYZİCO (Faz 8). `IYZICO_API_KEY` .env'de YOK; anahtarsızken
 * `NullPaymentProvider` tipli `PAYMENT_NOT_CONFIGURED` (HTTP 503) döner ve uç
 * ile servis katmanı HAZIR durur. Arayüz bu kodu görünce "ödeme henüz açık
 * değil" der, beyaz ekran görmez.
 *
 * 🔴 Webhook HAM GÖVDE ile doğrulanır. JSON'a çevrilip yeniden dizeye dönen
 * gövde farklı bayt üretir; Fastify'ın ham gövdeyi saklaması `main.ts`'te
 * açıkça yapılandırıldı.
 *
 * Olay idempotency'si: şemada olay tablosu yok → Redis `SETNX` + 7 gün TTL.
 * İstemciden gelen HİÇBİR plan bilgisine güvenilmez; plan yalnızca
 * sağlayıcının bildirdiği fiyat planı referansından çözülür.
 */
/**
 * İyzico'nun abonelik webhook olayları.
 *
 * ⚠️ Stripe'ın altı olayının karşılığı BU İKİSİ. İyzico "abonelik güncellendi"
 * ya da "checkout tamamlandı" diye ayrı olay YAYINLAMIYOR:
 *
 * | Stripe                          | İyzico karşılığı                        |
 * |---------------------------------|-----------------------------------------|
 * | `checkout.session.completed`    | YOK → tarayıcı `callbackUrl`'e döner    |
 * | `invoice.paid`                  | `subscription.order.success`            |
 * | `invoice.payment_failed`        | `subscription.order.failure`            |
 * | `customer.subscription.*`       | YOK → olay sonrası abonelik SORULUR     |
 *
 * Bu yüzden ilk satın alma bağlantısı webhook'la değil dönüş adresiyle
 * kuruluyor (`POST /billing/iyzico/callback/:state`) ve abonelik durumu her
 * olaydan sonra `GET /v2/subscription/subscriptions/{ref}` ile taze okunuyor.
 */
export const IYZICO_WEBHOOK_EVENTS = [
  'subscription.order.success',
  'subscription.order.failure',
] as const;
export type IyzicoWebhookEvent = (typeof IYZICO_WEBHOOK_EVENTS)[number];

export const checkoutSchema = z.object({
  plan: z.enum(['STARTER', 'GROWTH', 'PRO']),
  interval: z.enum(['monthly', 'annual']),
  /// Ödeme sonrası dönülecek panel rotası — açık yönlendirmeye karşı
  /// yalnızca `/dashboard` altı.
  returnTo: z
    .string()
    .max(200)
    .regex(/^\/dashboard(\/[\w\-/[\]]*)?$/)
    .default('/dashboard/billing'),
});
export type CheckoutInput = z.infer<typeof checkoutSchema>;

export interface CheckoutResponse {
  url: string;
  expiresAt: string | null;
}

export interface PortalResponse {
  url: string;
}

/**
 * FT8 — abonelik iptali.
 *
 * 🔴 Neden ayrı bir UÇ, neden "portalda hallolur" değil: Stripe'ın müşteri
 * portalının iyzico'da karşılığı YOK. İyzico'nun sunduğu tek self-servis
 * ekran KART GÜNCELLEME formu (`/billing/portal` onu açıyor); iptal yalnızca
 * API'den yapılıyor. Uç olmasaydı kullanıcı ürünü ancak destekle
 * bırakabilirdi — bu, iptali zorlaştırmanın kabul edilemez biçimi.
 *
 * ⚠️ İyzico'da "dönem sonunda iptal" YOK: `cancel` çağrısı aboneliği ANINDA
 * bitiriyor. Erişimi anında kesmiyoruz — `currentPeriodEnd`e kadar plan
 * limitleri duruyor, sonra bakım işi (`credit-rollover`) TRIAL'a düşürüyor.
 * Kullanıcı ödediği dönemi kullanır.
 */
export interface CancelSubscriptionResponse {
  status: SubscriptionStatus;
  /// Erişimin gerçekten biteceği an — ödenmiş dönemin sonu.
  accessUntil: string;
  /// Sağlayıcı tarafındaki iptal başarılı mı. `false` ise satır bizde iptal
  /// işaretlendi ama sağlayıcıda tahsilat sürebilir; destek görsün diye
  /// gizlenmiyor.
  providerCanceled: boolean;
}

// ═══════════════════════════════════════════════════════════════════════════
// Maliyet tablosu (KR6 / KS2)
// ═══════════════════════════════════════════════════════════════════════════

/// Kaynak `enums.ts`'teki `CREDIT_COST_BY_CREATIVE_TYPE`; burada yalnız yanıt
/// şekli. İki yerde sayı tutulmaz — kullanıcıya gösterilen fiyat ile düşülen
/// kredi aynı olmalı.
export interface CostTableRow {
  creativeType: string;
  credits: number;
  /// Sağlayıcısı var mı — yoksa arayüz "yakında" gösterir.
  available: boolean;
  labelKey: string;
}

// ═══════════════════════════════════════════════════════════════════════════
// Hata kodları
// ═══════════════════════════════════════════════════════════════════════════

export const BillingErrorCode = {
  SUBSCRIPTION_MISSING: 'SUBSCRIPTION_MISSING',
  CREDIT_BALANCE_MISSING: 'CREDIT_BALANCE_MISSING',
  /// `details: { required, available }`.
  INSUFFICIENT_CREDITS: 'INSUFFICIENT_CREDITS',
  /// Rezervasyon zaten commit ya da refund edilmiş.
  RESERVATION_ALREADY_SETTLED: 'RESERVATION_ALREADY_SETTLED',
  RESERVATION_NOT_FOUND: 'RESERVATION_NOT_FOUND',
  /// Ödeme sağlayıcısı yapılandırılmamış (IYZICO_API_KEY yok). HTTP 503.
  PAYMENT_NOT_CONFIGURED: 'PAYMENT_NOT_CONFIGURED',
  /// Sağlayıcı istek reddetti ya da ulaşılamadı. HTTP 503 — sağlayıcının ham
  /// hata metni İSTEMCİYE GİTMEZ (jeton/kimlik parçası taşıyabilir), loga gider.
  PAYMENT_PROVIDER_ERROR: 'PAYMENT_PROVIDER_ERROR',
  /// Webhook imzası doğrulanamadı. HTTP 400 — gövde loglanmaz.
  WEBHOOK_SIGNATURE_INVALID: 'WEBHOOK_SIGNATURE_INVALID',
  /**
   * Ödeme dönüşündeki `state` bulunamadı: süresi dolmuş (30 dk), zaten
   * kullanılmış ya da uydurulmuş. HTTP 400.
   *
   * Tek kullanımlık olması ŞART — aynı dönüş adresini ikinci kez açan biri
   * başkasının aboneliğini kendi kiracısına bağlayamamalı.
   */
  CHECKOUT_STATE_INVALID: 'CHECKOUT_STATE_INVALID',
  /// Grace süresi bitti; otomasyon ve kreatif kapalı.
  GRACE_EXPIRED: 'GRACE_EXPIRED',
  /// Abonelik iptal/süresi dolmuş.
  SUBSCRIPTION_INACTIVE: 'SUBSCRIPTION_INACTIVE',
  /// Plan limitine takıldı (hesap/üye/kredi). `details` hangisi olduğunu der.
  PLAN_LIMIT_REACHED: 'PLAN_LIMIT_REACHED',
  /// Ajan günlük mesaj limiti doldu.
  AGENT_DAILY_LIMIT: 'AGENT_DAILY_LIMIT',
  /// Faturalama işlemi yalnız OWNER'a açık.
  OWNER_ONLY: 'OWNER_ONLY',
  UNKNOWN_PACK: 'UNKNOWN_PACK',
} as const;
export type BillingErrorCode = (typeof BillingErrorCode)[keyof typeof BillingErrorCode];

// ═══════════════════════════════════════════════════════════════════════════
// /admin/plans — plan kataloğu yönetimi (Faz 8)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * PLAN KATALOĞU İKİ KAYNAKLI VE BU BİLİNÇLİ.
 *
 * ┌─────────────────────┬────────────────────────────────────────────────────┐
 * │ `plan_catalog` tablo│ ÇALIŞMA ZAMANI KAYNAĞI — admin buradan düzenler     │
 * │ `plans.ts` `PLANS`  │ VARSAYILAN — tablo boşsa/satır eksikse buna düşülür │
 * └─────────────────────┴────────────────────────────────────────────────────┘
 *
 * Neden bu yön: tablo BUGÜN 0 satır ve tek kaynak yapılsaydı fiyatlandırma
 * sayfası boş açılırdı — hem panelde hem pazarlama sayfasında. Ters yön
 * (kod kaynak, tablo yalnızca gösterim) ise admin ekranını yalancıya
 * çevirirdi: kaydedilen değeri hiçbir kod okumazdı.
 *
 * Düşme SATIR BAZINDA: tabloda GROWTH varsa GROWTH tablodan, PRO yoksa PRO
 * koddan okunur. Tablo bazında düşseydik tek eksik satır tüm admin
 * düzenlemelerini görünmez kılardı.
 *
 * 🔴 FİYAT DEĞİŞİKLİĞİ MEVCUT ABONELİKLERİ ETKİLEMEZ. Limitler abonelik
 * satırına AÇILIRKEN kopyalanıyor (`Subscription.adSpendLimit` vd.);
 * buradaki düzenleme yalnız BUNDAN SONRAKİ abonelikleri ve karşılaştırma
 * ekranını değiştirir. Kullanıcı $49'a aldığı kotayı biz tabloyu
 * değiştirdik diye kaybetmemeli.
 */
export interface AdminPlanCatalogRow {
  tier: PlanTier;
  monthlyUsd: number | null;
  yearlyUsd: number | null;
  spendCapUsd: number | null;
  credits: number | null;
  maxAdAccounts: number | null;
  maxMembers: number | null;
  /// Sağlayıcıdaki fiyat planı referansı (iyzico `pricingPlanReferenceCode`).
  providerPriceIdMonthly: string | null;
  providerPriceIdYearly: string | null;
  published: boolean;
  sortOrder: number;
  updatedAt: string | null;
  /**
   * 🔴 Satır TABLODAN mı geldi yoksa `plans.ts` VARSAYILANINDAN mı.
   *
   * Ekranda görünmek zorunda: admin düzenlemediği bir satırı düzenlenmiş
   * sanmamalı ve "kaydet"e basana kadar tabloda bir kaydı olmadığını
   * bilmeli. `false` olan satırın `updatedAt`i de null.
   */
  persisted: boolean;
}

export interface AdminPlanCatalogResponse {
  plans: AdminPlanCatalogRow[];
  /// Tabloda kaç satır var. 0 ise ekran "katalog henüz tohumlanmadı" der.
  persistedCount: number;
  /// Yıllık indirim oranı — kod sabiti, tablodan düzenlenmiyor (§7).
  annualDiscount: number;
}

/**
 * Plan satırı güncelleme. Tüm alanlar OPSİYONEL: ekran tek bir hücreyi
 * değiştirebilmeli, formun tamamını geri göndermek zorunda kalmamalı.
 *
 * 🔴 `tier` GÖVDEDE YOK — yoldan geliyor. Gövdede de olsaydı "STARTER'ı
 * güncelle" diyen bir istek PRO'yu değiştirebilirdi.
 *
 * 🔴 `published` BURADA YOK: ayrı uçta (`PUT /admin/plans/:tier/published`).
 * Yayından kaldırma fiyat düzenlemesinden farklı bir karar ve denetim
 * kaydında ayrı görünmeli — "kim fiyatı değiştirdi" ile "kim planı
 * satıştan kaldırdı" iki ayrı soru.
 */
export const adminPlanCatalogUpdateSchema = z
  .object({
    // Negatif fiyat yok; `null` = özel fiyat (Enterprise).
    monthlyUsd: z.number().nonnegative().max(100_000).nullable().optional(),
    yearlyUsd: z.number().nonnegative().max(1_000_000).nullable().optional(),
    spendCapUsd: z.number().nonnegative().max(100_000_000).nullable().optional(),
    // -1 = sınırsız (Enterprise). `isUnlimited()` negatifi böyle okuyor.
    credits: z.number().int().min(-1).max(1_000_000).nullable().optional(),
    maxAdAccounts: z.number().int().min(-1).max(10_000).nullable().optional(),
    maxMembers: z.number().int().min(-1).max(10_000).nullable().optional(),
    providerPriceIdMonthly: z.string().max(200).nullable().optional(),
    providerPriceIdYearly: z.string().max(200).nullable().optional(),
    sortOrder: z.number().int().min(0).max(1000).optional(),
  })
  // Boş gövde sessizce "başarılı" dönerdi; admin kaydettiğini sanır, hiçbir
  // şey değişmez.
  .refine((body) => Object.keys(body).length > 0, {
    message: 'En az bir alan gönderilmeli',
  });
export type AdminPlanCatalogUpdateInput = z.infer<typeof adminPlanCatalogUpdateSchema>;

export const adminPlanPublishSchema = z.object({
  published: z.boolean(),
});
export type AdminPlanPublishInput = z.infer<typeof adminPlanPublishSchema>;
